import { TeacherGoogleSyncStatus } from "@/api/teacherGoogleSync";
import { Notice } from "@/components/Notice";
import { ClasswizeModal } from "@/components/ui/Modal";
import { PrimaryButton } from "@/js/components/shared/button/PrimaryButton";
import { useTeacherGoogleSyncMutation, useTeacherGoogleSyncStatusQuery } from "@/query/teacherGoogleSyncQuery";
import { Box, ModalBody, ModalFooter, Spinner, Text } from "@chakra-ui/react";
import moment from "moment";
import ms from "ms";
import React from "react";
import { useTranslation } from "react-i18next";
import { useInterval } from "react-use";

const dateTimeFormatter = new Intl.DateTimeFormat([], {
	timeStyle: "short",
	dateStyle: "short",
});

// Note: SYNC_STATUS_POLL needs to be long enough that the sync system will likely have persisted the sync status after starting the sync. The status is not created atomically with starting the sync - it happens shortly afterwards instead.
// A data race still exists with the delay, but 10 seconds should be enough to avoid it showing unless there's a sync system incident.
const SYNC_STATUS_POLL = ms("10s");

export interface TeacherGoogleSyncStatusProps {
	close: () => void;
}

/**
 * TeacherGoogleSyncStatus should be conditionally rendered in the parent to open/close the modal.
 */
export const TeacherGoogleSyncModal: React.FC<TeacherGoogleSyncStatusProps> = (props) => {
	const { t } = useTranslation();

	const { data, isLoading, isError, refetch: refetchStatus } = useTeacherGoogleSyncStatusQuery();
	const syncMutation = useTeacherGoogleSyncMutation();

	useInterval(
		() => {
			refetchStatus();
		},
		data?.status === "running" ? SYNC_STATUS_POLL : null
	);

	const runGoogleSync = (force?: boolean) => {
		if (!force && (data?.status === "running" || syncMutation.isLoading)) {
			return;
		}
		syncMutation.mutate();
	};

	const renderSyncButton = () => {
		if (data?.status === "running") {
			return (
				<PrimaryButton
					className="mui-flat-button"
					disabled
					label={
						<Box display="flex" alignItems="center">
							<Spinner mr={2} />
							<span>{t("googleSyncModal.syncing")}</span>
						</Box>
					}
				/>
			);
		} else {
			return <PrimaryButton className="mui-flat-button" label={t("googleSyncModal.sync")} onClick={() => runGoogleSync()} />;
		}
	};

	const showSyncResult = data?.status === "finished" || data?.status === "error";

	return (
		<ClasswizeModal size="lg" title={t("googleSyncModal.title")} isOpen={true} onClose={props.close}>
			{isLoading ? (
				<ModalBody display="flex" flexDirection="column" alignItems="center" justifyContent="center" py={10}>
					<Spinner size="xl" mb={4} />
					<Text>{t("googleSyncModal.loadingSyncStatus")}</Text>
				</ModalBody>
			) : isError ? (
				<ModalBody display="flex" flexDirection="column" py={4}>
					<Notice status="error">
						<Text>{t("googleSyncModal.loadError")}</Text>
					</Notice>
				</ModalBody>
			) : (
				<>
					<ModalBody>
						{syncMutation.isError ? (
							<Notice status="error" mb={3}>
								<Text>{t("googleSyncModal.startError")}</Text>
							</Notice>
						) : null}
						<Text mb={showSyncResult ? 3 : undefined}>{t("googleSyncModal.body")}</Text>
						{showSyncResult && <SyncResult data={data} />}
					</ModalBody>
					<ModalFooter mt={8} p={0}>
						{renderSyncButton()}
						{data?.status === "running" && moment(data.startTime).isBefore(moment().subtract(15, "minutes")) && (
							<PrimaryButton className="mui-flat-button" label={t("googleSyncModal.restartSync")} onClick={() => runGoogleSync(true)} />
						)}
					</ModalFooter>
				</>
			)}
		</ClasswizeModal>
	);
};

type SyncResultProps = {
	data: Extract<TeacherGoogleSyncStatus, { status: "finished" } | { status: "error" }>;
	className?: string;
};

const SyncResult: React.FC<SyncResultProps> = ({ data, className }) => {
	const { t } = useTranslation();

	// Normalize error messages: ensure first letter is uppercase and message ends with a full stop.
	const formatError = (msg: string) => {
		let s = msg.trim();
		if (s.length === 0) return s;
		s = s[0].toUpperCase() + s.slice(1);
		if (s[s.length - 1] !== ".") s = `${s}.`;
		return s;
	};

	const formatDuration = (start: Date, end: Date) => {
		const duration = moment.duration(end.getTime() - start.getTime());
		return `${duration.minutes()}m ${duration.seconds()}s`;
	};

	const startTimeStr = data.startTime ? dateTimeFormatter.format(data.startTime) : "";
	const durationStr = data.startTime && data.endTime ? formatDuration(data.startTime, data.endTime) : "";

	return (
		<div className={className}>
			<Text decoration="underline">{t("googleSyncModal.latestDetails")}:</Text>
			<div style={{ backgroundColor: "#e8e8e8" }}>
				<ul>
					{data.status === "finished" ? (
						<li>{t("googleSyncModal.statusSuccess")}</li>
					) : (
						<li style={{ color: "red" }}>{t("googleSyncModal.statusFailed")}</li>
					)}
					<li>
						{t("googleSyncModal.started")}
						{startTimeStr}
					</li>
					<li>
						{t("googleSyncModal.duration")}
						{durationStr}
					</li>
					{data.status === "finished" && (
						<React.Fragment>
							<li>
								{t("googleSyncModal.classroomsMatched")}
								{data.classroomsMatched}
							</li>
							<li>
								{t("googleSyncModal.studentsMatched")}
								{data.studentsMatched}
							</li>
						</React.Fragment>
					)}
					{data.status === "error" && (
						<li>
							{t("googleSyncModal.error")}
							{formatError(data.errorMessage)}
						</li>
					)}
				</ul>
			</div>
		</div>
	);
};
